/*
 * Adapted from `ControlPoint` in the React Flow Pro "Editable Edge" example
 * (https://pro.reactflow.dev/examples/react/editable-edge), by webkid GmbH,
 * under the xyflow Pro License. The point model and the keyboard and
 * context-menu controls come from that example. The look, the drag, and the
 * double-click removal follow the builder design.
 */
import { Box } from '@mui/material';
import { type XYPosition } from '@xyflow/react';
import { type KeyboardEvent, type PointerEvent } from 'react';

import { COLORS } from '@/styles/tokens/colors';

/** The width of an inactive point, a hollow circle, in canvas pixels. */
const INACTIVE_SIZE = 10;
/** The width of an active point, a filled square, in canvas pixels. */
const ACTIVE_SIZE = 9;
/** The distance that an arrow key moves an active point, in canvas pixels. */
const KEYBOARD_STEP = 5;

const KEY_STEPS: Record<string, XYPosition> = {
  ArrowLeft: { x: -KEYBOARD_STEP, y: 0 },
  ArrowRight: { x: KEYBOARD_STEP, y: 0 },
  ArrowUp: { x: 0, y: -KEYBOARD_STEP },
  ArrowDown: { x: 0, y: KEYBOARD_STEP },
};

/** React Flow does not pan or drag the canvas from these elements. */
const NO_CANVAS_GESTURE = 'nodrag nopan';

/** Finds the points on the canvas. The line toolbar does not cover them. */
export const LINE_HANDLE_SELECTOR = '[data-line-handle]';

export interface LineControlPointProps {
  point: XYPosition;
  /**
   * An active point is a bend of the line. An inactive point marks where a
   * drag changes the line, such as the middle of a segment.
   */
  active: boolean;
  label: string;
  cursor: string;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  /** Moves an active point by `step`, in canvas pixels. */
  onMove?: (step: XYPosition) => void;
  /** Removes an active point from the line. */
  onRemove?: () => void;
}

/**
 * A point on a selected line that the user drags to route the line. The user
 * can focus an active point, move it with the arrow keys, and remove it with
 * Delete, Backspace, a right-click, or a double-click.
 */
export function LineControlPoint({
  point,
  active,
  label,
  cursor,
  onPointerDown,
  onMove,
  onRemove,
}: LineControlPointProps) {
  const size = active ? ACTIVE_SIZE : INACTIVE_SIZE;
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const step = KEY_STEPS[event.key];
    if (step && onMove) {
      event.preventDefault();
      onMove(step);
    } else if (
      (event.key === 'Delete' || event.key === 'Backspace') &&
      onRemove
    ) {
      // React Flow deletes the selected line on these keys.
      event.stopPropagation();
      onRemove();
    }
  };

  return (
    <Box
      role="button"
      tabIndex={active ? 0 : -1}
      aria-label={label}
      data-line-handle
      className={NO_CANVAS_GESTURE}
      onPointerDown={(event) => event.button === 0 && onPointerDown(event)}
      onKeyDown={handleKeyDown}
      onDoubleClick={onRemove}
      onContextMenu={(event) => {
        if (!onRemove) return;
        event.preventDefault();
        onRemove();
      }}
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        transform: `translate(-50%, -50%) translate(${point.x}px, ${point.y}px)`,
        borderRadius: active ? '2px' : '50%',
        border: `1.5px solid ${COLORS.BLUE[600]}`,
        backgroundColor: active ? COLORS.BLUE[600] : COLORS.PRIMITIVES.WHITE,
        pointerEvents: 'all',
        cursor,
      }}
    />
  );
}
